import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  DURATION_ALL_VALUE,
  DURATION_FILTER_OPTIONS,
  type DurationRange,
} from '@/utils/topic-duration';
import { getCategoryStyle } from '@/utils/topic-category-style';
import {
  CONTENT_CATEGORY_LABELS,
  type ContentCategory,
} from '@shared/api.interface';
import { TOPIC_FILTER_LABELS } from './topic-filter-copy';

interface TopicFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 点“看这 N 个主题”：不传时只关闭弹层 */
  onSeeResults?: () => void;
  durationRanges: DurationRange[];
  category: ContentCategory | '';
  resultCount: number;
  onDurationToggle: (value: DurationRange | typeof DURATION_ALL_VALUE) => void;
  onCategoryChange: (value: ContentCategory | '') => void;
  onClear: () => void;
  /** 各分类在其它筛选条件下的主题数；'' 表示全部 */
  categoryCounts?: Partial<Record<ContentCategory | '', number>>;
  /** 已登录时显示“只看未领取”开关 */
  showUnclaimedToggle?: boolean;
  unclaimedOnly?: boolean;
  onUnclaimedOnlyChange?: (value: boolean) => void;
}

const optionClass = (active: boolean) =>
  `inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border px-2 text-sm transition-colors ${
    active
      ? 'border-cta bg-orange-50 font-semibold text-cta'
      : 'border-border bg-white font-medium text-foreground hover:border-orange-300'
  }`;

export function TopicFilterDrawer({
  open,
  onOpenChange,
  onSeeResults,
  durationRanges,
  category,
  resultCount,
  onDurationToggle,
  onCategoryChange,
  onClear,
  categoryCounts,
  showUnclaimedToggle = false,
  unclaimedOnly = false,
  onUnclaimedOnlyChange,
}: TopicFilterDrawerProps) {
  const hasActiveFilters = durationRanges.length > 0 || !!category || unclaimedOnly;
  const countLabel = (key: ContentCategory | '') =>
    categoryCounts && categoryCounts[key] !== undefined ? (
      <span className="text-xs font-normal opacity-60">{categoryCounts[key]}</span>
    ) : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[86vh] rounded-t-3xl">
        <DrawerHeader className="border-b border-border px-5 pb-3 pt-4 text-left">
          <DrawerTitle className="text-xl font-semibold">改一下条件</DrawerTitle>
          <DrawerDescription className="sr-only">按题材和时长筛选</DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 px-5 py-5">
            {showUnclaimedToggle && onUnclaimedOnlyChange && (
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl bg-background px-4 py-3.5">
                <span>
                  <span className="block text-[15px] font-semibold text-foreground">只看未领取</span>
                  <span className="block text-xs text-muted-foreground">已经领过的主题先藏起来，只看还没领的</span>
                </span>
                <Switch
                  checked={unclaimedOnly}
                  onCheckedChange={onUnclaimedOnlyChange}
                  aria-label="只看未领取"
                  className="data-[state=checked]:bg-cta"
                />
              </label>
            )}
            <section>
              <h3 className="mb-3 text-[15px] font-semibold text-foreground">{TOPIC_FILTER_LABELS.category}</h3>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => onCategoryChange('')} className={optionClass(category === '')}>
                  全部 {countLabel('')}
                </button>
                {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onCategoryChange(key as ContentCategory)}
                    className={optionClass(category === key)}
                  >
                    <span
                      aria-hidden
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: getCategoryStyle(key).dot }}
                    />
                    {label}
                    {countLabel(key as ContentCategory)}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-[15px] font-semibold text-foreground">
                {TOPIC_FILTER_LABELS.duration}
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">{TOPIC_FILTER_LABELS.durationHint}</span>
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {DURATION_FILTER_OPTIONS.map((option) => {
                  const isActive =
                    option.value === DURATION_ALL_VALUE
                      ? durationRanges.length === 0
                      : durationRanges.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onDurationToggle(option.value)}
                      className={optionClass(isActive)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </section>

          </div>
        </ScrollArea>

        <DrawerFooter className="grid grid-cols-[1fr_2fr] gap-2.5 border-t border-border bg-white px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClear}
            disabled={!hasActiveFilters}
            className="h-12 rounded-xl border-orange-200 text-[15px] font-semibold text-orange-800 hover:bg-orange-50"
          >
            清空
          </Button>
          <Button
            type="button"
            onClick={() => (onSeeResults ? onSeeResults() : onOpenChange(false))}
            className="h-12 rounded-xl bg-cta text-[15px] font-semibold text-cta-foreground hover:bg-cta-hover"
          >
            看这 {resultCount} 个主题
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
